import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { shouldReloadOnControllerChange } from './utils/swReload';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN, // no-op if not set
  environment: process.env.NODE_ENV || 'production',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
});

// ── Inject admin-managed scripts into the page ────────────────────
const injectScripts = async () => {
  const API = process.env.REACT_APP_API_URL || (window.location.hostname !== 'localhost' ? '/api' : 'http://localhost:4000/api');
  const locations = ['head', 'body_start', 'body_end'];
  for (const location of locations) {
    try {
      const res = await fetch(`${API}/scripts/inject?location=${location}`, { signal: AbortSignal.timeout(3000) });
      if (!res.ok) continue; // silently skip on any error
      const code = await res.text();
      if (!code.trim()) continue;
      const el = document.createElement('div');
      el.innerHTML = code;
      const nodes = Array.from(el.childNodes).map(n => n.cloneNode(true));
      if (location === 'head') {
        nodes.forEach(n => document.head.appendChild(n));
      } else if (location === 'body_start') {
        const root = document.getElementById('root');
        nodes.forEach(n => root?.parentNode?.insertBefore(n, root));
      } else {
        nodes.forEach(n => document.body.appendChild(n));
      }
    } catch {
      // Always silent — never break the app or show errors
    }
  }
};

injectScripts();

// Global error capture — Sentry handles these automatically when DSN is set;
// console.error keeps them visible in Railway logs regardless
window.addEventListener('error', (e) => {
  console.error('[CherryPKD] Uncaught error:', e.message);
  if (!process.env.REACT_APP_SENTRY_DSN) return;
  Sentry.captureException(e.error || new Error(e.message));
});
window.addEventListener('unhandledrejection', (e) => {
  console.error('[CherryPKD] Unhandled rejection:', e.reason);
  if (!process.env.REACT_APP_SENTRY_DSN) return;
  Sentry.captureException(e.reason instanceof Error ? e.reason : new Error(String(e.reason)));
});

// Register service worker for PWA (offline support + push notifications).
// Reload once when a new SW replaces an older one so auction min-bid / listing JS
// is not the previous precached chunk (YOUR BID MIN $11.001). Not on first install
// (nothing stale; clientsClaim still fires controllerchange) and never on
// /verify-email or /reset-password, where a reload re-sends the one-shot token.
if ('serviceWorker' in navigator) {
  const hadController = !!navigator.serviceWorker.controller;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(reg => {
      reg.update().catch(() => {});
    }).catch(() => {});
  });
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!shouldReloadOnControllerChange({ hadController, pathname: window.location.pathname, alreadyRefreshing: refreshing })) return;
    refreshing = true;
    window.location.reload();
  });
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
